import type { AssistantDataParts } from "../../../../api/src/shared/assistant-ui-message";
import {
  type GptChatInstance,
  uiMessageActions,
  uiMessagePhase,
  uiMessageSearchCounts,
  uiMessageSourceTotal,
} from "../../domains/gpt/hooks/useGptChat";

/**
 * Where the answer of the tour question stands, for the notes of the wizard
 * (docs/juiscigpt-wizard.md). Note 1 follows the first three stages (REQ-06):
 * screen 1 while the engine searches, screen 2 from its first read article
 * with the search counts (OD-10, REQ-33), screen 3 while it writes. Note 2 shows once the answer is complete
 * (REQ-07). A failed or interrupted run, or a reply that is not an answer (a
 * clarification), shows no note.
 */
export type WizardAnswerStage =
  | { kind: "searching" }
  | {
      kind: "reading";
      readCount: number;
      // Null until the engine streams the counts of its first search.
      searchCounts: AssistantDataParts["searchCounts"] | null;
    }
  | { kind: "writing"; readCount: number }
  | { kind: "answered" }
  | { kind: "stopped" };

export function wizardAnswerStage(
  chat: Pick<GptChatInstance, "messages" | "status">
): WizardAnswerStage {
  if (chat.status === "error") return { kind: "stopped" };
  const last = chat.messages.at(-1);
  // The question is sent and nothing came back yet.
  if (last === undefined || last.role !== "assistant")
    return { kind: "searching" };
  // The Sources tab total comes with a published answer, and only with it.
  if (uiMessageSourceTotal(last) !== null) return { kind: "answered" };
  // The turn ended without an answer: a clarification, or a stopped run.
  if (chat.status === "ready") return { kind: "stopped" };
  const readCount = uiMessageActions(last).actions.filter(
    (action) => action.kind === "read"
  ).length;
  if (uiMessagePhase(last) === "writing") return { kind: "writing", readCount };
  if (readCount > 0)
    return {
      kind: "reading",
      readCount,
      searchCounts: uiMessageSearchCounts(last),
    };
  return { kind: "searching" };
}
